import { Add } from "@mui/icons-material"
import { Box, Button, Stack, Typography } from "@mui/material"
import { DataGrid, GridActionsCellItem, GridColumns, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid"
import { useMemo, useState } from "react";
import { getTelecomValues } from "src/utils/fhir";
import uuidv4 from "src/utils/uuidv4"
import ContactModal from "./ContactModal";
import produce from "immer";
import { WrappedOrganization } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization";
import { checkAclValidation } from "src/careflow/ph-core/admin/permissions/util/permission.utils";
import crsAcls from "src/careflow/ph-core/admin/permissions/util/crs/crsAcls";
import { getCodeableConceptDisplayName } from "src/careflow/helpers/common";

const FhirOrganizationContacts = ({
  organization,
  update,
  translate
}: {
  organization: WrappedOrganization | null,
  update: Function,
  translate: any
}) => {

  const [open, setOpen] = useState(false);
  const [row, setRow] = useState(null);

  const permissions = {
    add: checkAclValidation({ acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CONTACTS.ADD] }),
    edit: checkAclValidation({ acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CONTACTS.ADD] }),
    delete: checkAclValidation({ acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CONTACTS.ADD] })
  };

  const columns: GridColumns = [
    {
      field: 'name',
      headerName: `${translate('details.general.name')}`,
      valueGetter: (params: GridValueGetterParams) => {
        const name = `${params.row?.name?.given?.[0]} ${params.row?.name?.family}`;
        return name;
      },
      editable: false,
      sortable: true,
      filterable: false,
      disableColumnMenu: true,
      flex: 2
    },
    {
      field: 'contact',
      headerName: `${translate('details.general.contact')}`,
      renderCell: (params: GridRenderCellParams) => {
        const contact = params?.row ?? null;
        const mainEmail = getTelecomValues("email", contact?.telecom ?? [])?.[0];
        const mainPhone = getTelecomValues("phone", contact?.telecom ?? [])?.[0];
        return (
          <Box>
            <Typography variant="subtitle2" noWrap>
              {mainPhone}
            </Typography>
            <Typography variant="body2" noWrap>
              {mainEmail}
            </Typography>
          </Box>
        )
      },
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 2,
    },
    {
      field: 'type',
      headerName: `${translate('details.general.type')}`,
      renderCell: (params: GridRenderCellParams) => {
        const type = params?.row?.purpose ?? null;
        return (
          <Typography variant="body2">
            {getCodeableConceptDisplayName(type)}
          </Typography>
        )
      },
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 2,
    },
    {
      field: 'edit',
      headerName: '',
      type: 'actions',
      flex: 1,
      cellClassName: 'actions',
      getActions: ({ row }) => {
        return [
          permissions.edit ? (
            <GridActionsCellItem
              label="Edit"
              className="textPrimary"
              onClick={() => {
                setRow(row);
                setOpen(true);
              }}
              color="inherit"
              showInMenu
            />
          ) : (
            <></>
          ),
          permissions.delete ? (
            <GridActionsCellItem
              label="Delete"
              className="textPrimary"
              onClick={() => {
                // saveId(id);
              }}
              color="inherit"
              showInMenu
            />
          ) : (
            <></>
          ),
        ];
      },
    },
  ];

  const mapOrgContact = useMemo(() =>
    organization?.contact?.map((e) => ({
      id: uuidv4(),
      ...e,
    }))
  ,[organization?.contact]);

  const handleSave = async (data) => {
    if (!organization) return;
  
    await update(
      produce(organization, (draft) => {
        if (!draft.contact) draft.contact = [];
  
        const index = draft.contact.findIndex(c => c.id === data.id);
  
        if (index >= 0) {
          draft.contact[index] = {
            ...draft.contact[index],
            ...data,
          };
        } else {
          draft.contact.push({
            id: data.id ?? uuidv4(),
            ...data,
          });
        }
      })
    );
    setOpen(false);
  };

  return (
    <>
      <Stack direction="column" sx={{ padding: 3 }}>
        <Typography
          style={{ display: 'flex', justifyContent: 'space-between' }}
          variant="button"
          sx={{ textTransform: 'uppercase' }}
        >
          <Button
            variant="contained"
            disabled={!permissions.add}
            color="primary"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            style={{ position: 'absolute', top: '20px', right: '25px' }}
          >
            {translate('details.general.newContact')}
          </Button>
        </Typography>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={mapOrgContact ?? []}
            columns={columns}
            editMode="row"
            pageSize={5}
            getRowId={() => uuidv4()}
            autoHeight
            sx={{
              '& .MuiDataGrid-row': {
                borderBottom: '1px solid #EAECF0',
                '&:nth-of-type(odd)': {
                  backgroundColor: '#FAFAFA',
                },
                '&:hover': {
                  backgroundColor: '#F5F6F7',
                },
              },
            }}
          />
        </Box>
      </Stack>
      <ContactModal
        open={open}
        contact={row}
        translate={translate}
        onClose={() => {
          setRow(null);
          setOpen(false);
        }}
        updateOrganization={handleSave}
      />
    </>
  )
}

export default FhirOrganizationContacts;